/**
 * Pre-built mock community for Zlin (Czech Republic).
 *
 * These get seeded into the React Query cache at app startup so
 * all pages have realistic data to display without a running backend.
 *
 * Data is based on: docs/RESEARCH_2026-02-09_czech_zlin_energy_data.md
 */

// ---------------------------------------------------------------------------
// Stable UUIDs (deterministic, valid v4-ish format)
// ---------------------------------------------------------------------------
const ZLIN_ID = 'a1b2c3d4-1111-4aaa-b111-000000000001'

let _seq = 100
const uid = (prefix) => {
  _seq++
  const hex = _seq.toString(16).padStart(4, '0')
  return `${prefix}-${hex}-4ccc-9ddd-eeeeeeee${hex}`
}

// ---------------------------------------------------------------------------
// ZLIN COMMUNITY  (larger, urban, 8 buildings)
// ---------------------------------------------------------------------------
// Annual load: ~65,000 kWh   |  PV: 42 kWp (12 rooftop + 30 farm)
// Battery: 50 kWh   |  SCR ~82%   |  SSR ~53%
// PV/Load = 42000/65000 = 0.646  (deficit community, load > generation)
// ---------------------------------------------------------------------------

const zlinBuildings = [
  {
    id: uid('zb'), building_id: 'Panelak A', building_type: 'residential',
    floor_area_m2: 400, num_units: 4, num_floors: 2, year_built: 1978, energy_class: 'D',
    building_assets: [
      { id: uid('za'), asset_type: 'rooftop_pv', name: 'Rooftop PV', specs: { capacity_kwp: 5, tilt_angle: 30, azimuth: 180, efficiency: 85 } },
    ],
    datasets: [
      { id: uid('zd'), name: 'Panelak A Load 2025', dataset_type: 'building_load_profile', kind: 'load', metadata: { time_resolution: 'hourly', row_count: 8760, start_date: '2025-01-01', end_date: '2025-12-31' } },
    ],
  },
  {
    id: uid('zb'), building_id: 'Residence B', building_type: 'residential',
    floor_area_m2: 140, num_units: 1, num_floors: 2, year_built: 1995, energy_class: 'C',
    building_assets: [
      { id: uid('za'), asset_type: 'rooftop_pv', name: 'Rooftop PV', specs: { capacity_kwp: 4, tilt_angle: 35, azimuth: 180, efficiency: 85 } },
    ],
    datasets: [
      { id: uid('zd'), name: 'Residence B Load 2025', dataset_type: 'building_load_profile', kind: 'load', metadata: { time_resolution: 'hourly', row_count: 8760, start_date: '2025-01-01', end_date: '2025-12-31' } },
    ],
  },
  {
    id: uid('zb'), building_id: 'Residence C', building_type: 'residential',
    floor_area_m2: 120, num_units: 1, num_floors: 1, year_built: 2005, energy_class: 'B',
    building_assets: [],
    datasets: [
      { id: uid('zd'), name: 'Residence C Load 2025', dataset_type: 'building_load_profile', kind: 'load', metadata: { time_resolution: 'hourly', row_count: 8760, start_date: '2025-01-01', end_date: '2025-12-31' } },
    ],
  },
  {
    id: uid('zb'), building_id: 'Residence D', building_type: 'residential',
    floor_area_m2: 150, num_units: 1, num_floors: 2, year_built: 1990, energy_class: 'C',
    building_assets: [],
    datasets: [
      { id: uid('zd'), name: 'Residence D Load 2025', dataset_type: 'building_load_profile', kind: 'load', metadata: { time_resolution: 'hourly', row_count: 8760, start_date: '2025-01-01', end_date: '2025-12-31' } },
    ],
  },
  {
    id: uid('zb'), building_id: 'Residence E', building_type: 'residential',
    floor_area_m2: 130, num_units: 1, num_floors: 1, year_built: 2010, energy_class: 'B',
    building_assets: [
      { id: uid('za'), asset_type: 'rooftop_pv', name: 'Rooftop PV', specs: { capacity_kwp: 3, tilt_angle: 35, azimuth: 190, efficiency: 85 } },
    ],
    datasets: [
      { id: uid('zd'), name: 'Residence E Load 2025', dataset_type: 'building_load_profile', kind: 'load', metadata: { time_resolution: 'hourly', row_count: 8760, start_date: '2025-01-01', end_date: '2025-12-31' } },
    ],
  },
  {
    id: uid('zb'), building_id: 'Municipal Office', building_type: 'municipal',
    floor_area_m2: 800, num_units: 1, num_floors: 2, year_built: 1985, energy_class: 'D',
    building_assets: [
      { id: uid('za'), asset_type: 'hvac', name: 'HVAC System', specs: { rated_power_kw: 12, controllable: 'yes', t_min_c: 18, t_max_c: 26, mode: 'both', cop_heating: 3.5, cop_cooling: 3.0, thermal_mass_kwh_per_c: 4.0 } },
    ],
    datasets: [
      { id: uid('zd'), name: 'Municipal Office Load 2025', dataset_type: 'building_load_profile', kind: 'load', metadata: { time_resolution: 'hourly', row_count: 8760, start_date: '2025-01-01', end_date: '2025-12-31' } },
    ],
  },
  {
    id: uid('zb'), building_id: 'Residence G', building_type: 'residential',
    floor_area_m2: 160, num_units: 1, num_floors: 2, year_built: 2015, energy_class: 'B',
    building_assets: [
      { id: uid('za'), asset_type: 'heat_pump', name: 'Air-Source Heat Pump', specs: { rated_power_kw: 8, cop: 3.2, controllable: 'yes', t_min_c: 18, t_max_c: 24, mode: 'heating', thermal_mass_kwh_per_c: 2.5 } },
    ],
    datasets: [
      { id: uid('zd'), name: 'Residence G Load 2025', dataset_type: 'building_load_profile', kind: 'load', metadata: { time_resolution: 'hourly', row_count: 8760, start_date: '2025-01-01', end_date: '2025-12-31' } },
    ],
  },
  {
    id: uid('zb'), building_id: 'Residence H', building_type: 'residential',
    floor_area_m2: 145, num_units: 1, num_floors: 2, year_built: 2018, energy_class: 'A',
    building_assets: [
      { id: uid('za'), asset_type: 'ev_charger', name: 'EV Charger (11 kW)', specs: { max_power_kw: 11, num_evs: 1, daily_energy_per_ev_kwh: 15, controllable: 'yes', arrival_hour: 18, departure_hour: 7, battery_capacity_kwh: 60, min_soc_pct: 80, arrival_soc_pct: 30 } },
    ],
    datasets: [
      { id: uid('zd'), name: 'Residence H Load 2025', dataset_type: 'building_load_profile', kind: 'load', metadata: { time_resolution: 'hourly', row_count: 8760, start_date: '2025-01-01', end_date: '2025-12-31' } },
    ],
  },
]

const zlinCommunityAssets = [
  {
    id: uid('zca'), name: 'Community Solar Farm', asset_type: 'solar_pv_farm', owner_scope: 'community',
    specs: { capacity_kwp: 30, efficiency: 19, tilt_angle: 35, azimuth: 180 },
  },
  {
    id: uid('zca'), name: 'Community Battery (LFP)', asset_type: 'community_battery', owner_scope: 'community',
    specs: { capacity_kwh: 50, p_charge_kw: 25, p_discharge_kw: 25, efficiency_charge: 94, efficiency_discharge: 94, soc_min: 10, soc_max: 90 },
  },
]

const zlinCommunityDatasets = [
  { id: uid('zcd'), name: 'Community PV Generation 2025', dataset_type: 'pv_generation_profile', kind: 'pv', metadata: { time_resolution: 'hourly', row_count: 8760, start_date: '2025-01-01', end_date: '2025-12-31' } },
  { id: uid('zcd'), name: 'Import Tariff ERU 2025', dataset_type: 'energy_tariff_data', kind: 'tariff', metadata: { time_resolution: 'hourly', row_count: 8760, start_date: '2025-01-01', end_date: '2025-12-31' } },
  { id: uid('zcd'), name: 'Export Tariff OTE 2025', dataset_type: 'feed_in_tariff_data', kind: 'tariff', metadata: { time_resolution: 'hourly', row_count: 8760, start_date: '2025-01-01', end_date: '2025-12-31' } },
  { id: uid('zcd'), name: 'Solar Irradiance Zlin 2025', dataset_type: 'weather_solar', kind: 'weather', metadata: { time_resolution: 'hourly', row_count: 8760, start_date: '2025-01-01', end_date: '2025-12-31' } },
]

export const zlinCommunity = {
  id: ZLIN_ID,
  community_id: ZLIN_ID,
  name: 'Zlin Energy Community',
  timezone: 'Europe/Prague',
  currency: 'CZK',
  created_at: '2025-06-15T10:00:00Z',
  updated_at: '2026-02-08T14:30:00Z',
  metadata: {
    country: 'Czech Republic',
    location: 'Zlin, Zlin Region',
    description: 'Urban energy community in Zlin with 8 buildings, community PV farm, and shared battery storage. Mix of residential apartments and family houses with a municipal office.',
    status: 'active',
    latitude: 49.2264,
    longitude: 17.6688,
    market_type: 'regulated',
    grid_connection_type: 'low_voltage',
    import_tariff: 5.50,
    export_tariff: 2.00,
    grid_charges: 1.50,
  },
  buildings: zlinBuildings,
  community_assets: zlinCommunityAssets,
  community_datasets: zlinCommunityDatasets,
  // Alias for backward compat
  datasets: zlinCommunityDatasets,
}

// ---------------------------------------------------------------------------
// Exports
// ---------------------------------------------------------------------------
export const MOCK_COMMUNITIES = [zlinCommunity]
export { ZLIN_ID }

// ---------------------------------------------------------------------------
// Helper: flatten buildings for useBuildings hook
// Adds metadata flags expected by BuildingsSetupPage
// ---------------------------------------------------------------------------
function prepareBuildingsForHook(buildings) {
  return buildings.map(b => {
    const assetTypes = (b.building_assets || []).map(a => a.asset_type)
    return {
      ...b,
      metadata: {
        num_units: b.num_units,
        num_floors: b.num_floors,
        year_built: b.year_built,
        has_rooftop_pv: assetTypes.includes('rooftop_pv'),
        has_battery: assetTypes.includes('battery'),
        has_ev_charger: assetTypes.includes('ev_charger'),
        has_heat_pump: assetTypes.includes('heat_pump'),
        has_hvac: assetTypes.includes('hvac'),
      },
    }
  })
}

// ---------------------------------------------------------------------------
// Helper: flatten all datasets for useDatasets hook
// Adds building_id, resolution, quality, time_start, time_end
// ---------------------------------------------------------------------------
function prepareDatasetsForHook(buildings, communityDatasets) {
  const allDatasets = []

  // Community-level datasets (no building_id)
  for (const ds of communityDatasets) {
    allDatasets.push({
      ...ds,
      building_id: null,
      resolution: ds.metadata?.time_resolution || 'hourly',
      time_start: ds.metadata?.start_date || '2025-01-01',
      time_end: ds.metadata?.end_date || '2025-12-31',
      quality: { row_count: ds.metadata?.row_count || 8760 },
    })
  }

  // Building-level datasets (set building_id to the building's UUID)
  for (const b of buildings) {
    for (const ds of (b.datasets || [])) {
      allDatasets.push({
        ...ds,
        building_id: b.id,
        resolution: ds.metadata?.time_resolution || 'hourly',
        time_start: ds.metadata?.start_date || '2025-01-01',
        time_end: ds.metadata?.end_date || '2025-12-31',
        quality: { row_count: ds.metadata?.row_count || 8760 },
      })
    }
  }

  return allDatasets
}

/**
 * Seed Zlin community into a React Query client.
 * Called once at app startup from main.jsx.
 */
export function seedMockCommunities(queryClient) {
  // NOTE: Do NOT seed the communities list cache — let it come from the real API.
  // Only seed the Zlin detail so it's available as a demo when navigated to directly.

  // Seed individual community detail (same key shape as useCommunity hook)
  queryClient.setQueryData(['communities', ZLIN_ID], zlinCommunity)

  // Seed buildings list (useBuildings hook: ['buildings', communityId])
  queryClient.setQueryData(['buildings', ZLIN_ID], prepareBuildingsForHook(zlinBuildings))

  // Seed community assets list (useAssets hook: ['communityAssets', communityId])
  queryClient.setQueryData(['communityAssets', ZLIN_ID], zlinCommunityAssets)

  // Seed datasets list (useDatasets hook: ['datasets', { community_id }])
  queryClient.setQueryData(
    ['datasets', { community_id: ZLIN_ID }],
    prepareDatasetsForHook(zlinBuildings, zlinCommunityDatasets)
  )
}
